'use client';

import { Box, Container, Flex, Heading, Text } from '@chakra-ui/react';
import cn from 'classnames';
import React, { useCallback, useRef, useState } from 'react';

import { ReactComponent as ArrowBackwardCircle } from '@/app/_icons/ArrowBackwardCircleDark.svg';
import { ReactComponent as ArrowForwardCircle } from '@/app/_icons/ArrowForwardCircleDark.svg';
import { getImageLink } from '@/services/link.service';
import { FeatureType } from '@/types/new-cars/new-cars.types';
import { Prettify } from '@/types/utils/common.types';

import CustomImage from '../custom-image/CustomImage';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import Styles from './FeaturesSlider.module.scss';

type DescriptionRefs = {
  [key: string]: HTMLElement;
};

type Props = {
  content: Array<Prettify<FeatureType & { key: string }>>;
  vertical?: boolean;
  subHeading?: string;
  heading?: string;
};

const verticalKeys = {
  desktopKey: 'image-680-680',
  mobileKey: 'image-430-430',
};

const horizontalKeys = {
  desktopKey: 'image-1330-400',
  mobileKey: 'image-430-220',
};

const FeaturesSlider = ({ content, vertical, subHeading, heading }: Props) => {

  const validContent = content?.filter(
    (item) => item && (item.image_id || (item.title && item.description)),
  );

  const descriptionViewport = useRef<HTMLDivElement>(null);
  const descriptionRefs = useRef<DescriptionRefs>({});
  const [activeIndex, setActiveIndex] = useState(0);

  const isElementInView = (viewport: DOMRect, element: DOMRect) => {
    return (
      element.top >= viewport.top &&
      element.left >= viewport.left &&
      element.bottom <= viewport.bottom &&
      element.right <= viewport.right
    );
  };

  const getOutOfViewDistance = (viewport: DOMRect, element: DOMRect) => {
    const result = {
      x: 0,
      y: 0,
    };

    if (isElementInView(viewport, element)) {
      return null;
    }

    if (element.right > viewport.right && !vertical)
      result.x = element.right - viewport.right;
    if (element.left < viewport.left && !vertical)
      result.x = element.left - viewport.left;
    if (element.bottom > viewport.bottom && vertical)
      result.y = element.bottom - viewport.bottom;
    if (element.top < viewport.top && vertical)
      result.y = element.top - viewport.top;

    return result;
  };

  const scrollToDescription = (element?: HTMLElement) => {
    if (!element) return;
    const viewWindow = descriptionViewport.current;

    if (viewWindow) {
      const viewportRect = viewWindow.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const outOfViewDistance = getOutOfViewDistance(viewportRect, elementRect);

      if (outOfViewDistance) {
        viewWindow.scrollBy({
          top: outOfViewDistance.y,
          left: outOfViewDistance.x,
          behavior: 'smooth',
        });
      }
    }
  };

  const scrollLeft = useCallback(() => {
    if (activeIndex <= 0) {
      return;
    }
    setActiveIndex((oldIndex) => oldIndex - 1);
    const contentBlock = validContent[activeIndex - 1];

    scrollToDescription(descriptionRefs.current[contentBlock?.key as string]);
  }, [validContent, activeIndex, descriptionViewport, descriptionRefs]);

  const scrollRight = useCallback(() => {
    if (activeIndex >= validContent?.length - 1) {
      return;
    }
    setActiveIndex((oldIndex) => oldIndex + 1);
    const contentBlock = validContent[activeIndex + 1];

    scrollToDescription(descriptionRefs.current[contentBlock?.key as string]);
  }, [validContent, activeIndex, descriptionViewport, descriptionRefs]);

  if (!validContent || validContent.length === 0) {
    return null;
  }
  const handleDescriptionClick = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    const element = event.target as HTMLElement;

    if (descriptionViewport.current && element) {
      scrollToDescription(element);
    }
    setActiveIndex(index);
  };

  const FeatureImages = () => {
    return (
      <Box
        width="fit-content"
        marginX="auto"
        marginBottom={{ base: 4, lg: 0 }}
      >
        <Box className={Styles.showcaseWindow}>
          <ul className={Styles.showcaseContainer}>
            {validContent?.map((item, index) => {
              if (!item.image_id) {
                return null;
              }

              const imageSrc = getImageLink(item.image_id);

              if (!imageSrc) {
                return null;
              }

              return (
                <li
                  key={`${item.key}-showcase`}
                  className={cn(
                    Styles.showcaseBlock,
                    index === activeIndex ? Styles.active : null,
                  )}
                >
                  {index === activeIndex && (
                    <CustomImage
                      imageSrc={imageSrc}
                      imageKeys={vertical ? verticalKeys : horizontalKeys}
                      alt={item.title || 'Feature Image'}
                      width={1280}
                      height={681}
                      loading="eager"
                      quality={75}
                      className={Styles.featureImage}
                    />
                    )}
                </li>
              );
            })}
          </ul>
        </Box>
      </Box>
    );
  };

  const hasValidImages = validContent.some(
    (item) => item && item.image_id && getImageLink(item.image_id as string),
  );

  return (
    <Box
      className={cn(Styles.featureSliderContainer, vertical && Styles.vertical)}
    >
      {!vertical && (heading || subHeading) && (
        <Container
          className={Styles.headerContent}
          maxW={'1280'}
          width={'100%'}
          mb={{ base: 4, md: 12 }}
          px={{ base: 6, md: 4 }}
        >
          <Flex justifyContent={'space-between'} alignItems={'flex-end'}>
            <Box className={Styles.headingElement}>
              {subHeading && (
                <Text
                  fontSize={{ base: 'xs', lg: 'sm' }}
                  textTransform={'uppercase'}
                  mb={{ base: 4, md: 2 }}
                >
                  {subHeading}
                </Text>
              )}
              {heading && (
                <Heading as="h2" className={Styles.titleLg}>
                  {heading}
                </Heading>
              )}
            </Box>

            <Box className={Styles.desktopShow}>
              <Box className={Styles.navigationControl}>
                <Box className={Styles.pagination}>
                  <Text className={Styles.currentPage}>{activeIndex + 1}</Text>
                  <Text className={Styles.separator}>/</Text>
                  <Text className={Styles.totalPage}>
                    {validContent?.length}
                  </Text>
                </Box>

                <Box className={Styles.navigation}>
                  <button
                    onClick={scrollLeft}
                    name="Previous"
                    className={Styles.buttonPrev}
                    role="button"
                  >
                    <ArrowBackwardCircle aria-label="Previous" />
                  </button>
                  <button
                    onClick={scrollRight}
                    name="Next"
                    className={Styles.buttonNext}
                    role="button"
                  >
                    <ArrowForwardCircle aria-label="Next" />
                  </button>
                </Box>
              </Box>
            </Box>
          </Flex>
        </Container>
      )}

      <Container
        maxW={vertical ? '1280' : '100%'}
        display={vertical ? 'flex' : 'block'}
        width={'100%'}
        className={cn(
          vertical ? Styles.verticalContainer : Styles.blockContainer,
        )}
      >
        <Box className={cn(vertical && Styles.featureBlock)}>
          {vertical && (heading || subHeading) && (
            <>
              <Box
                className={Styles.headingElement}
                mb={6}
                marginX={{ base: 6, lg: 0 }}
              >
                {subHeading && (
                  <Text
                    fontSize={{ base: 'xs', lg: 'sm' }}
                    textTransform={'uppercase'}
                    mb={{ base: 4, md: 2 }}
                  >
                    {subHeading}
                  </Text>
                )}
                {heading && (
                  <Heading as="h2" className={Styles.titleLg}>
                    {heading}
                  </Heading>
                )}
              </Box>
              <Box className={Styles.mobileShow}>
                <Box className={Styles.navigationControl}>
                  <Box className={Styles.pagination}>
                    <Text className={Styles.currentPage}>
                      {activeIndex + 1}
                    </Text>
                    <Text className={Styles.separator}>/</Text>
                    <Text className={Styles.totalPage}>
                      {validContent?.length}
                    </Text>
                  </Box>

                  <Box className={Styles.navigation}>
                    <button
                      onClick={scrollLeft}
                      name="Previous"
                      className={Styles.buttonPrev}
                      role="button"
                    >
                      <ArrowBackwardCircle aria-label="Previous" />
                    </button>
                    <button
                      onClick={scrollRight}
                      name="Next"
                      className={Styles.buttonNext}
                      role="button"
                    >
                      <ArrowForwardCircle aria-label="Next" />
                    </button>
                  </Box>
                </Box>
              </Box>
            </>
          )}
          <Box className={Styles.mobileShow}>
            {hasValidImages && <FeatureImages />}
            {!vertical && (
              <Box className={Styles.navigationControl}>
                <Box className={Styles.pagination}>
                  <Text className={Styles.currentPage}>{activeIndex + 1}</Text>
                  <Text className={Styles.separator}>/</Text>
                  <Text className={Styles.totalPage}>
                    {validContent?.length}
                  </Text>
                </Box>

                <Box className={Styles.navigation}>
                  <button
                    onClick={scrollLeft}
                    name="Previous"
                    className={Styles.buttonPrev}
                    role="button"
                  >
                    <ArrowBackwardCircle aria-label="Previous" />
                  </button>
                  <button
                    onClick={scrollRight}
                    name="Next"
                    className={Styles.buttonNext}
                    role="button"
                  >
                    <ArrowForwardCircle aria-label="Next" />
                  </button>
                </Box>
              </Box>
            )}
          </Box>
          <Box
            className={cn(
              Styles.descriptionViewport,
              vertical ? Styles.vertical : '',
            )}
            ref={descriptionViewport}
            mb={vertical ? { base: 0, md: 1 } : { base: 0, md: 12 }}
          >
            <ul className={Styles.descriptionContainer}>
              {validContent?.map((item, index) => (
                <li
                  key={`${item.key}-description`}
                  ref={(element) => {
                    if (element) {
                      descriptionRefs.current[item.key] = element;
                    } else {
                      delete descriptionRefs.current[item.key];
                    }
                  }}
                  className={cn(
                    Styles.descriptionBlock,
                    index == activeIndex ? Styles.active : null,
                  )}
                  onClick={(e) => handleDescriptionClick(e, index)}
                >
                  <Box className={Styles.featureBar} />

                  <Box className={Styles.headingElement}>
                    {item.title && (
                      <Heading
                        as="h3"
                        className={cn(Styles.titleMd, Styles.featureTitle)}
                        mb={4}
                      >
                        {item.title}
                      </Heading>
                    )}
                    {item.description && (
                      <Box
                        className={Styles.featureDescription}
                        dangerouslySetInnerHTML={{
                          __html: item.description,
                        }}
                      ></Box>
                    )}
                  </Box>
                </li>
              ))}
            </ul>
          </Box>
          {vertical && (
            <Box className={Styles.desktopShow}>
              <Box className={Styles.navigationControl}>
                <Box className={Styles.navigation}>
                  <button
                    onClick={scrollLeft}
                    name="Previous"
                    className={Styles.buttonPrev}
                    role="button"
                  >
                    <ArrowBackwardCircle aria-label="Previous" />
                  </button>
                  <button
                    onClick={scrollRight}
                    name="Next"
                    className={Styles.buttonNext}
                    role="button"
                  >
                    <ArrowForwardCircle aria-label="Next" />
                  </button>
                </Box>
                <Box className={Styles.desktopShow}>
                  <Box className={Styles.pagination}>
                    <Text className={Styles.currentPage}>
                      {activeIndex + 1}
                    </Text>
                    <Text className={Styles.separator}>/</Text>
                    <Text className={Styles.totalPage}>
                      {validContent?.length}
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        <Box className={Styles.desktopShow}>
          {hasValidImages && <FeatureImages />}
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturesSlider;
